// Public-side newsroom result zone for /electionday.
//
// This is the newsroom's confirmed call — kept structurally and
// visually separate from the experimental sample's number (which
// lives in StatsPanel further down the page). The framing is
// deliberate: the editorial figure is what Lovin Malta is willing
// to put its name on; the experimental panel is the live sampling
// experiment, labelled indicative throughout.
//
// Composition:
//   • National call headline + percentages (PL / PN / other)
//   • Per-district editorial results table (PL / PN / OTHER votes,
//     status flag, last-updated)
//   • Static STV explainer (5 seats per district, quota-based) —
//     intentionally NOT an automated seat projection.

import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import type {
  DistrictEditorialResult,
  DistrictStatus,
} from '../../data/electionday-editorial-types';

const ALL_DISTRICTS = Array.from({ length: 13 }, (_, i) => `District ${i + 1}`);

const STATUS_LABEL: Record<DistrictStatus, string> = {
  pending: 'Pending',
  counting: 'Counting',
  first_count: '1st count complete',
  final: 'Final',
};

export default function EditorialResultZone() {
  const { data, isLoading } = useElectionDayEditorial();
  const { nationalCall, districts, updatedAt } = data;

  return (
    <section className="electionday-editorial" aria-label="Newsroom result">
      <header className="electionday-editorial__head">
        <span className="electionday-editorial__kicker">
          Newsroom result · confirmed by Lovin Malta editors
        </span>
        <h2 className="electionday-editorial__title">
          {nationalCall.party
            ? `${nationalCall.label} — ${nationalCall.party}`
            : nationalCall.label}
        </h2>
        {nationalCall.party && nationalCall.calledAt && (
          <p className="electionday-editorial__called-at">
            Called {formatStamp(nationalCall.calledAt)}
          </p>
        )}
        {nationalCall.note && (
          <p className="electionday-editorial__note">{nationalCall.note}</p>
        )}
      </header>

      <div className="electionday-editorial__share">
        <ShareTile
          party="PL"
          pct={nationalCall.pl_pct}
          active={nationalCall.party === 'PL'}
        />
        <ShareTile
          party="PN"
          pct={nationalCall.pn_pct}
          active={nationalCall.party === 'PN'}
        />
        <ShareTile party="OTHER" pct={nationalCall.other_pct} active={false} />
      </div>

      <h3 className="electionday-editorial__subhead">
        Per-district first-count results
      </h3>
      <div className="electionday-editorial__table-wrap">
        <table className="electionday-editorial__table">
          <thead>
            <tr>
              <th>District</th>
              <th>PL</th>
              <th>PN</th>
              <th>Other</th>
              <th>Valid</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {ALL_DISTRICTS.map((d) => (
              <DistrictRow
                key={d}
                district={d}
                row={districts[d] ?? null}
              />
            ))}
          </tbody>
        </table>
      </div>

      <StvNote />

      <p className="electionday-editorial__meta">
        Newsroom figures · last touched {formatStamp(updatedAt)}{' '}
        {isLoading && <span className="electionday-editorial__dot">·</span>}
      </p>
    </section>
  );
}

interface ShareTileProps {
  party: 'PL' | 'PN' | 'OTHER';
  pct: number | null;
  active: boolean;
}

function ShareTile({ party, pct, active }: ShareTileProps) {
  return (
    <div
      className={`electionday-editorial__share-tile electionday-editorial__share-tile--${party.toLowerCase()}${
        active ? ' is-called' : ''
      }`}
    >
      <span className="electionday-editorial__share-label">{party}</span>
      <span className="electionday-editorial__share-pct">
        {pct === null ? '—' : `${pct.toFixed(1)}%`}
      </span>
    </div>
  );
}

interface DistrictRowProps {
  district: string;
  row: DistrictEditorialResult | null;
}

function DistrictRow({ district, row }: DistrictRowProps) {
  if (!row) {
    return (
      <tr className="electionday-editorial__row electionday-editorial__row--empty">
        <th scope="row">{district}</th>
        <td>—</td>
        <td>—</td>
        <td>—</td>
        <td>—</td>
        <td>{STATUS_LABEL.pending}</td>
        <td>—</td>
      </tr>
    );
  }
  return (
    <tr
      className={`electionday-editorial__row electionday-editorial__row--${row.status}`}
    >
      <th scope="row">{district}</th>
      <td>{row.pl_votes !== null ? row.pl_votes.toLocaleString() : '—'}</td>
      <td>{row.pn_votes !== null ? row.pn_votes.toLocaleString() : '—'}</td>
      <td>{row.other_votes !== null ? row.other_votes.toLocaleString() : '—'}</td>
      <td>
        {row.total_valid !== null ? row.total_valid.toLocaleString() : '—'}
      </td>
      <td>{STATUS_LABEL[row.status]}</td>
      <td>{row.enteredAt ? formatStamp(row.enteredAt) : '—'}</td>
    </tr>
  );
}

/** Plain-language STV explainer. Intentionally NOT an automated
 *  seat projector — Maltese 5-seat district STV with vote transfers
 *  is too brittle to project from first-count percentages alone,
 *  and the editorial team would rather call seats by hand after
 *  each count than have the page print a misleading auto-prediction
 *  next to confirmed editorial numbers. */
function StvNote() {
  return (
    <aside className="electionday-editorial__stv">
      <h4>How seats are allocated</h4>
      <p>
        Malta elects 5 MPs from each of the 13 districts using Single
        Transferable Vote. A candidate is elected on the first count when
        their first-preference total meets the district&apos;s{' '}
        <em>Droop quota</em> — total valid votes divided by 6 (seats + 1),
        rounded up. Surplus votes from elected candidates and the votes
        of eliminated candidates transfer to next preferences across
        subsequent counts until 5 seats are filled.
      </p>
      <p>
        First-count percentages on this page are useful indicators but
        do <strong>not</strong> map directly to seat counts — transfers
        reshape the picture, especially in tight districts. Seat allocations
        will be entered manually by the newsroom as each district&apos;s
        counts are confirmed.
      </p>
    </aside>
  );
}

function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
